import * as React from "react";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const SDK_URL = process.env.REACT_APP_ORG_AGENT_SDK_URL;
const PUBLIC_KEY = process.env.REACT_APP_ORG_AGENT_PUBLIC_KEY;
// const DEMO_USER_JWT = process.env.REACT_APP_USER_JWT;
const INVEX_API_BASE_URL =
  process.env.REACT_APP_INVEX_API_BASE_URL ??
  "https://test-service-api.invexonline.com/api";

function getApiUrl() {
  if (process.env.REACT_APP_ORG_AGENT_API_URL) {
    return process.env.REACT_APP_ORG_AGENT_API_URL;
  }
  if (!SDK_URL) {
    throw new Error("REACT_APP_ORG_AGENT_SDK_URL is not defined");
  }
  return new URL(SDK_URL).origin;
}

export function AssistantV2() {
  const [ready, setReady] = React.useState(false);
  const [lastNavigation, setLastNavigation] = React.useState("");
  const [lastFormBuilder, setLastFormBuilder] = React.useState("");
  const containerRef = React.useRef(null);
  const { token: DEMO_USER_JWT } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    function onMessage({ data }) {
      const action = data?.action;
      const params = action?.params;

      if (data?.type === "org-agent:quicklink-navigation") {
        if (action?.autoOpen && action?.route?.link) {
          setLastNavigation(data.action?.route?.link ?? "");

          let finalLink = action.route?.link;

          if (params && Object.keys(params).length > 0) {
            const queryString = new URLSearchParams(params).toString();
            finalLink += finalLink.includes("?")
              ? `&${queryString}`
              : `?${queryString}`;
          }

          navigate(finalLink);
        }
      }
      if (data?.type === "org-agent:form-builder") {
        setLastFormBuilder(data.template?.title ?? "");

        navigate(
          "/requisition/forms/company?page=1&limit=42&formType=Organisation&q=",
          {
            state: {
              action: "naila-form-builder-create",
              payload: data?.template,
              timestamp: Date.now(),
            },
          },
        );
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  React.useEffect(() => {
    let mounted = true;
    let cleanup;

    async function boot() {
      const orgAgent = await loadOrgAgentSdk(SDK_URL);
      const client = orgAgent.createAgentClient({
        apiUrl: getApiUrl(),
        publicKey: PUBLIC_KEY,
        token: DEMO_USER_JWT,
        invexApiBaseUrl: INVEX_API_BASE_URL,
      });

      if (!containerRef.current || !mounted) return;

      cleanup = await client.mountChat(containerRef.current, {
        labels: {
          brandName: "Naila",
          greeting: "Hello",
          subtitle: "How can I help you today?",
          composerPlaceholder: "Ask Naila anything...",
          disclaimer:
            "Naila can make mistakes. Double-check critical ERP actions.",
        },
        categories: [
          { id: "dashboard", label: "Dashboard" },
          { id: "sales", label: "Sales" },
          { id: "accounts", label: "Accounts" },
          { id: "inventory", label: "Inventory" },
          { id: "hr", label: "HR" },
        ],
        suggestions: [
          {
            id: "customer-list",
            label: "Find the customer list",
            prompt: "Find the customer list page.",
            category: "sales",
          },
          {
            id: "create-invoice",
            label: "Take me to create invoice",
            prompt: "Take me to the create invoice page.",
            category: "sales",
          },
          {
            id: "analytics",
            label: "Show analytics options",
            prompt: "Show me analytics and dashboard options.",
            category: "dashboard",
          },
          {
            id: "employee-pages",
            label: "Find employee pages",
            prompt: "Find employee and HR pages.",
            category: "hr",
          },
          {
            id: "expense-form",
            label: "Create an expense form",
            prompt: "Create an expense reimbursement form.",
            category: "accounts",
          },
        ],
        theme: {
          accentColor: "#255fad",
          accentForeground: "#ffffff",
          radius: "28px",
          // logoUrl: "/logo.png",
        },
        onAction(action) {
          console.log(action);
          if (action.kind === "quicklink_navigation") {
            setLastNavigation(action.route.link);
          }
          if (action.kind === "form_builder") {
            setLastFormBuilder(action.template.title);
          }
        },
      });
      setReady(true);
    }

    void boot();

    return () => {
      mounted = false;
      cleanup?.unmount();
    };
  }, []);

  return <div ref={containerRef} />;
}

async function loadOrgAgentSdk(src) {
  if (hasUsableOrgAgent(window.OrgAgent)) {
    return window.OrgAgent;
  }
  delete window.OrgAgent;

  await new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[data-org-agent-sdk="${src}"]`,
    );
    if (existing) {
      if (!hasUsableOrgAgent(window.OrgAgent)) {
        delete window.OrgAgent;
        existing.remove();
      } else {
        resolve();
        return;
      }
    }

    const current = document.querySelector(
      `script[data-org-agent-sdk="${src}"]`,
    );
    if (current) {
      current.addEventListener("load", () => resolve(), { once: true });
      current.addEventListener(
        "error",
        () => reject(new Error(`Could not load SDK from ${src}`)),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.orgAgentSdk = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Could not load SDK from ${src}`));
    document.head.appendChild(script);
  });

  if (!hasUsableOrgAgent(window.OrgAgent)) {
    throw new Error(
      `SDK loaded from ${src}, but window.OrgAgent.createAgentClient was not found.`,
    );
  }

  return window.OrgAgent;
}

function hasUsableOrgAgent(value) {
  return typeof value?.createAgentClient === "function";
}
